<template>
  <div class="statDashboard">
    <div class="pond-stat">
      <!-- 出租方筛选（二级农发有子公司时显示） -->
      <div v-if="companyList.length" class="pond-company-filter">
        <span class="pond-company-label">出租方</span>
        <el-select
          v-model="selectedCompany"
          placeholder="请选择出租方"
          clearable
          size="small"
          style="width: 240px"
          @change="handleCompanyChange"
        >
          <el-option label="全部" value="" />
          <el-option
            v-for="c in companyList"
            :key="c.id"
            :label="c.name"
            :value="c.id"
          />
        </el-select>
      </div>

      <div class="pond-header">
        <h2 class="pond-header-title">鱼塘汇总 ({{ totalCount }})</h2>
      </div>

      <div class="pond-summary">
        <div
          class="pond-summary-card card-blue"
          :class="{ active: activeFilter === 'all' }"
          @click="setFilter('all')"
        >
          <div class="card-value">{{ totalCount }}</div>
          <div class="card-label">鱼塘总数</div>
        </div>
        <div
          class="pond-summary-card card-pink"
          :class="{ active: activeFilter === 'rented' }"
          @click="setFilter('rented')"
        >
          <div class="card-value">{{ rentedCount }}</div>
          <div class="card-label">已使用</div>
        </div>
        <div
          class="pond-summary-card card-green"
          :class="{ active: activeFilter === 'idle' }"
          @click="setFilter('idle')"
        >
          <div class="card-value">{{ idleCount }}</div>
          <div class="card-label">空闲中</div>
        </div>
        <div
          class="pond-summary-card card-red"
          :class="{ active: activeFilter === 'expiringSoon' }"
          @click="setFilter('expiringSoon')"
        >
          <div class="card-value">{{ expiringSoonCount }}</div>
          <div class="card-label">即将到期</div>
        </div>
      </div>

      <div v-loading="loading" class="pond-grid">
        <div
          v-for="item in detailList"
          :key="item.propertyId"
          class="pond-card"
          :class="{
            'pond-card--used': !item.expiringSoon && item.tenantName,
            'pond-card--idle': !item.tenantName,
            'pond-card--expiring': item.expiringSoon
          }"
        >
          <div
            v-if="item.expiringSoon && item.expireDate"
            class="pond-card-badge"
          >
            {{ formatExpireDate(item.expireDate) }}到期
          </div>

          <div class="pond-card-body">
            <div class="pond-card-code">{{ item.propertyCode }}</div>
            <div class="pond-card-area">面积 {{ formatArea(item.area) }}亩</div>

            <template v-if="!item.tenantName">
              <div class="pond-card-idle">
                已空闲{{ item.idleDays == null ? 0 : item.idleDays }}天
              </div>
            </template>
            <template v-else>
              <el-tooltip
                :content="item.tenantName"
                :disabled="!item.tenantName || item.tenantName.length <= 20"
                placement="top"
              >
                <div class="pond-card-company">{{ item.tenantName }}</div>
              </el-tooltip>
              <div class="pond-card-breed">
                品种：{{ item.breed || "草鱼、黄金鲫" }}
              </div>
            </template>
          </div>
        </div>
        <div
          v-if="!loading && detailList.length === 0"
          class="pond-empty"
        >
          暂无数据
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getStatSummary,
  getHierarchyChildren,
  leaseRequestError
} from "@/service/fishPondLease";
import { PropertyType } from "@/service/fishPondLeaseEnums";

export default {
  name: "StatDashboard",
  data() {
    return {
      loading: false,
      totalCount: 0,
      rentedCount: 0,
      idleCount: 0,
      expiringSoonCount: 0,
      detailList: [],
      activeFilter: "all",
      companyList: [],
      selectedCompany: ""
    };
  },
  mounted() {
    this.loadCompanies();
    this.loadData();
  },
  methods: {
    loadCompanies() {
      getHierarchyChildren()
        .then(res => {
          this.companyList = (res && res.data) || [];
        })
        .catch(() => {
          this.companyList = [];
        });
    },
    loadData() {
      this.loading = true;
      const params = {
        type: PropertyType.FishPond,
        statusFilter: this.activeFilter
      };
      if (this.selectedCompany) {
        params.queryEnterpriseId = this.selectedCompany;
      }
      getStatSummary(params)
        .then(res => {
          const data = (res && res.data) || {};
          this.totalCount = Number(data.totalCount) || 0;
          this.rentedCount = Number(data.rentedCount) || 0;
          this.idleCount = Number(data.idleCount) || 0;
          this.expiringSoonCount = Number(data.expiringSoonCount) || 0;
          this.detailList = data.detailList || [];
        })
        .catch(err => {
          this.$message.error(leaseRequestError(err));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    setFilter(filter) {
      this.activeFilter = filter;
      this.loadData();
    },
    handleCompanyChange() {
      this.loadData();
    },
    formatArea(area) {
      const n = Number(area);
      if (isNaN(n)) return "0";
      return String(n);
    },
    formatExpireDate(val) {
      if (!val) return "";
      const s = String(val);
      // 后端可能返回 2026-11-01T00:00:00 或 2026-11-01
      if (s.length >= 10) return s.substring(0, 10);
      return s;
    }
  }
};
</script>

<style lang="stylus" scoped>
.statDashboard
  height 100%

.pond-stat
  padding 20px 24px
  min-height calc(100vh - 140px)
  display flex
  flex-direction column
  background #f0f2f5
  box-sizing border-box

.pond-company-filter
  display flex
  align-items center
  gap 12px
  margin-bottom 20px
  flex-shrink 0

.pond-company-label
  font-size 14px
  font-weight 500
  color #1a1a1a
  white-space nowrap

.pond-header
  display flex
  align-items center
  justify-content space-between
  margin-bottom 20px
  flex-shrink 0

.pond-header-title
  font-size 18px
  font-weight 600
  color #1a1a1a
  margin 0

.pond-summary
  display flex
  gap 16px
  margin-bottom 20px
  flex-shrink 0

.pond-summary-card
  flex 1
  background #fff
  border-radius 10px
  padding 22px 20px
  text-align center
  cursor pointer
  border 1px solid #d8dce5
  transition all 0.2s
  position relative
  overflow hidden
  &:hover
    transform translateY(-2px)
    box-shadow 0 6px 20px rgba(60, 80, 240, 0.12)
  &.active
    border-color #3c50f0
    box-shadow 0 0 0 2px rgba(60, 80, 240, 0.15), 0 4px 16px rgba(60, 80, 240, 0.1)

.card-value
  font-size 32px
  font-weight 700
  line-height 1.2
  color #1a1a1a

.card-label
  font-size 13px
  margin-top 8px

.card-blue
  border-left 4px solid #3c50f0
  background #eef1ff
  .card-label
    color #4a5ed8

.card-pink
  border-left 4px solid #f08a6a
  background #fff3ee
  .card-label
    color #d87a5e

.card-green
  border-left 4px solid #5cb89b
  background #eef8f4
  .card-label
    color #4a9e84

.card-red
  border-left 4px solid #e85454
  background #fff0f0
  .card-label
    color #d04444

.pond-grid
  display grid
  grid-template-columns repeat(7, 1fr)
  gap 12px
  flex 1
  overflow auto
  align-content start
  min-height 120px

.pond-empty
  grid-column 1 / -1
  text-align center
  color #999
  padding 40px 0
  font-size 14px

.pond-card
  position relative
  background #fff
  border-radius 8px
  border 1px solid #e0e4ea
  padding 14px 12px
  text-align center
  min-height 90px
  display flex
  flex-direction column
  justify-content center
  transition box-shadow 0.2s
  &:hover
    box-shadow 0 2px 12px rgba(0, 0, 0, 0.06)

.pond-card--used
  border-left 4px solid #f08a6a
  background #fff3ee

.pond-card--idle
  border-left 4px solid #5cb89b
  background #eef8f4

.pond-card--expiring
  border-left 4px solid #e85454
  background #fff0f0

.pond-card-badge
  position absolute
  top -1px
  right -1px
  background #e85454
  color #fff
  font-size 11px
  padding 3px 10px
  border-radius 0 7px 0 8px
  white-space nowrap
  z-index 1

.pond-card-body
  display flex
  flex-direction column
  align-items center
  gap 6px

.pond-card-code
  font-size 13px
  font-weight 500
  color #1a1a1a

.pond-card-area
  font-size 13px
  color #7b8ba3

.pond-card-idle
  font-size 13px
  font-weight 500
  color #4a9e84

.pond-card-company
  font-size 14px
  font-weight 600
  color #1a1a1a
  max-width 100%
  overflow hidden
  text-overflow ellipsis
  white-space nowrap

.pond-card-breed
  font-size 12px
  color #7b8ba3

@media (max-width: 1400px)
  .pond-grid
    grid-template-columns repeat(5, 1fr)

@media (max-width: 1100px)
  .pond-grid
    grid-template-columns repeat(4, 1fr)
  .pond-summary
    flex-wrap wrap
  .pond-summary-card
    flex 1 1 calc(50% - 16px)
    min-width 140px

@media (max-width: 768px)
  .pond-grid
    grid-template-columns repeat(2, 1fr)
</style>
